import { supabase } from './supabase';
import { characteristics } from '../data/characteristics';
import { harmonicLevels } from '../data/harmonicLevels';
import type { Score } from '../types';
import type { HarmonicScore } from '../types/harmonic';
import type { Team, TeamMember, TeamAverageScores } from '../types/team';

export async function getPartnerIdForUser(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('partners')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  return data?.id || null;
}

export async function loadTeams(partnerId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('partner_id', partnerId)
    .order('type', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;

  const { data: memberCounts } = await supabase
    .from('team_members')
    .select('team_id');

  const countMap: Record<string, number> = {};
  (memberCounts || []).forEach(m => {
    countMap[m.team_id] = (countMap[m.team_id] || 0) + 1;
  });

  return (data || []).map(t => ({
    id: t.id,
    partnerId: t.partner_id,
    name: t.name,
    description: t.description,
    type: t.type as 'team' | 'department',
    parentId: t.parent_id,
    isActive: t.is_active,
    createdAt: new Date(t.created_at),
    updatedAt: new Date(t.updated_at),
    memberCount: countMap[t.id] || 0,
  }));
}

export async function loadTeamMembers(teamId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select(`
      id,
      team_id,
      user_id,
      role,
      added_at,
      users!team_members_user_id_fkey(full_name, email)
    `)
    .eq('team_id', teamId);

  if (error) throw error;

  const userIds = (data || []).map(m => m.user_id);

  let respondentMap: Record<string, { coachId: string; lastDate: string | null }> = {};
  let coachNameMap: Record<string, string> = {};
  let resultUserIds: Set<string> = new Set();

  if (userIds.length > 0) {
    const { data: respondents } = await supabase
      .from('respondents')
      .select('user_id, coach_id, last_assessment_date')
      .in('user_id', userIds);

    (respondents || []).forEach(r => {
      respondentMap[r.user_id] = {
        coachId: r.coach_id || '',
        lastDate: r.last_assessment_date,
      };
    });

    const coachIds = [...new Set(Object.values(respondentMap).map(r => r.coachId).filter(Boolean))];
    if (coachIds.length > 0) {
      const { data: coaches } = await supabase
        .from('coaches')
        .select('id, users!coaches_user_id_fkey(full_name, email)')
        .in('id', coachIds);

      (coaches || []).forEach(c => {
        coachNameMap[c.id] = (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown';
      });
    }

    const { data: results } = await supabase
      .from('results')
      .select('user_id')
      .in('user_id', userIds);

    (results || []).forEach(r => resultUserIds.add(r.user_id));
  }

  return (data || []).map(m => ({
    id: m.id,
    teamId: m.team_id,
    userId: m.user_id,
    role: m.role as 'member' | 'lead',
    addedAt: new Date(m.added_at),
    userName: (m.users as any)?.full_name || (m.users as any)?.email || 'Unknown',
    userEmail: (m.users as any)?.email || '',
    coachName: respondentMap[m.user_id]
      ? coachNameMap[respondentMap[m.user_id].coachId] || 'Unassigned'
      : 'Unassigned',
    hasResults: resultUserIds.has(m.user_id),
    lastAssessmentDate: respondentMap[m.user_id]?.lastDate
      ? new Date(respondentMap[m.user_id].lastDate!)
      : null,
  }));
}

export async function loadAvailableRespondents(
  partnerId: string,
  excludeTeamId: string
): Promise<{ userId: string; name: string; email: string }[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const { data: respondents } = await supabase
    .from('respondents')
    .select('user_id, users!respondents_user_id_fkey(full_name, email)')
    .in('coach_id', coachIds);

  if (!respondents || respondents.length === 0) return [];

  const { data: existingMembers } = await supabase
    .from('team_members')
    .select('user_id')
    .eq('team_id', excludeTeamId);

  const existingIds = new Set((existingMembers || []).map(m => m.user_id));

  return (respondents || [])
    .filter(r => !existingIds.has(r.user_id))
    .map(r => ({
      userId: r.user_id,
      name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
      email: (r.users as any)?.email || '',
    }));
}

export function computeTeamAverages(
  teamId: string,
  teamName: string,
  resultsData: { userId: string; scores: Score[]; harmonicScores: HarmonicScore[] }[]
): TeamAverageScores {
  if (resultsData.length === 0) {
    return {
      teamId,
      teamName,
      memberCount: 0,
      membersWithResults: 0,
      averageScores: characteristics.map(c => ({
        characteristicId: c.id,
        raw: 0,
        normalized: 0,
      })),
      averageHarmonicScores: harmonicLevels.map(l => ({
        levelId: l.id,
        raw: 0,
        normalized: 0,
      })),
    };
  }

  const charSums: Record<string, { raw: number; normalized: number; count: number }> = {};
  characteristics.forEach(c => {
    charSums[c.id] = { raw: 0, normalized: 0, count: 0 };
  });

  const harmSums: Record<number, { raw: number; normalized: number; count: number }> = {};
  harmonicLevels.forEach(l => {
    harmSums[l.id] = { raw: 0, normalized: 0, count: 0 };
  });

  resultsData.forEach(result => {
    (result.scores || []).forEach(score => {
      if (charSums[score.characteristicId]) {
        charSums[score.characteristicId].raw += score.raw;
        charSums[score.characteristicId].normalized += score.normalized;
        charSums[score.characteristicId].count += 1;
      }
    });

    (result.harmonicScores || []).forEach(score => {
      if (harmSums[score.levelId]) {
        harmSums[score.levelId].raw += score.raw;
        harmSums[score.levelId].normalized += score.normalized;
        harmSums[score.levelId].count += 1;
      }
    });
  });

  const averageScores: Score[] = characteristics.map(c => ({
    characteristicId: c.id,
    raw: charSums[c.id].count > 0
      ? Math.round(charSums[c.id].raw / charSums[c.id].count)
      : 0,
    normalized: charSums[c.id].count > 0
      ? Math.round((charSums[c.id].normalized / charSums[c.id].count) * 100) / 100
      : 0,
  }));

  const averageHarmonicScores: HarmonicScore[] = harmonicLevels.map(l => ({
    levelId: l.id,
    raw: harmSums[l.id].count > 0
      ? Math.round(harmSums[l.id].raw / harmSums[l.id].count)
      : 0,
    normalized: harmSums[l.id].count > 0
      ? Math.round((harmSums[l.id].normalized / harmSums[l.id].count) * 100) / 100
      : 0,
  }));

  return {
    teamId,
    teamName,
    memberCount: resultsData.length,
    membersWithResults: resultsData.length,
    averageScores,
    averageHarmonicScores,
  };
}

export async function loadTeamResults(teamId: string) {
  const { data: members } = await supabase
    .from('team_members')
    .select('user_id')
    .eq('team_id', teamId);

  if (!members || members.length === 0) return [];

  const userIds = members.map(m => m.user_id);

  const { data: results } = await supabase
    .from('results')
    .select('user_id, scores, harmonic_scores, completed_at, version')
    .in('user_id', userIds)
    .order('completed_at', { ascending: false });

  const latestByUser: Record<string, {
    userId: string;
    scores: Score[];
    harmonicScores: HarmonicScore[];
  }> = {};

  (results || []).forEach(r => {
    if (!latestByUser[r.user_id]) {
      latestByUser[r.user_id] = {
        userId: r.user_id,
        scores: r.scores as any,
        harmonicScores: r.harmonic_scores as any,
      };
    }
  });

  return Object.values(latestByUser);
}
